import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { BookOpen, Lock, Sparkle, Lightning, WifiHigh } from "@phosphor-icons/react";

/**
 * /print/event-card        — A5 portrait leaflet (148 × 210 mm)
 * /print/event-badge       — A6 portrait badge-back (105 × 148 mm)
 *
 * Both QR codes resolve to /card?promo=hl1may which:
 *   - serves the digital business card
 *   - sets the localStorage flag to unlock the event consultation banner
 *
 * The dotted "TAP HERE" zone is sized for a 22mm round NFC sticker.
 * Programme each NFC tag with the same URL and stick it over the zone.
 */

const SIZES = {
  a5: {
    label: "A5",
    sub: "leaflet · 2 per A4",
    width: "148mm", height: "210mm",
    qr: 110, qrPad: 8,
    nfc: 70, // px on screen; 22mm physical
    headlineSize: "text-[28px]",
    bodySize: "text-[12.5px]",
    tilesGap: "gap-2.5",
    paddingX: "px-8",
    paddingTop: "pt-9",
  },
  a6: {
    label: "A6",
    sub: "badge back · 4 per A4 · 50+ run",
    width: "105mm", height: "148mm",
    qr: 132, qrPad: 6,
    nfc: 64,
    headlineSize: "text-[20px]",
    bodySize: "text-[10.5px]",
    tilesGap: "gap-1.5",
    paddingX: "px-5",
    paddingTop: "pt-5",
  },
};

const URL_BASE = "https://appistry.tech/event/";

export default function EventCard() {
  const { variant } = useParams();
  const [searchParams] = useSearchParams();
  const code = (searchParams.get("code") || "hl1may").toLowerCase();
  const minutes = Number(searchParams.get("minutes")) || 30;
  const URL_TARGET = `${URL_BASE}${code}`;
  const size = SIZES[variant === "badge" ? "a6" : "a5"];

  return (
    <>
      {/* Print-only stylesheet — page size matches the variant */}
      <style>{`
        @page { size: ${variant === "badge" ? "A6" : "A5"} portrait; margin: 0; }
        @media print {
          html, body { background: #FFFFFF !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .print-card { box-shadow: none !important; margin: 0 !important; page-break-after: always; }
        }
        .print-card {
          width: ${size.width};
          height: ${size.height};
          background: #FFFFFF;
          color: #0A0A0A;
          font-family: 'Outfit', system-ui, -apple-system, sans-serif;
          position: relative;
          overflow: hidden;
        }
        .print-mono { font-family: ui-monospace, 'SF Mono', Menlo, monospace; }
        .signal-blue { color: #0570DE; }
        .signal-bg { background-color: #0570DE; }
      `}</style>

      <div className="min-h-screen bg-zinc-100 py-10">
        {/* Toolbar (hidden in print) */}
        <div className="no-print max-w-2xl mx-auto px-6 mb-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Event handout</p>
            <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {size.label} · {size.sub}
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              {variant === "badge"
                ? "For the back of A6 event badges. Bigger QR for arm's-length scanning, NFC tap zone for stickered tags."
                : "For leaflet handouts. Cmd/Ctrl+P → Save as PDF or print to cardstock."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href={variant === "badge" ? "/print/event-card" : "/print/event-card/badge"} className="px-4 py-2.5 border border-zinc-300 bg-white text-sm font-semibold" data-testid="size-toggle">
              Switch to {variant === "badge" ? "A5 leaflet" : "A6 badge back"}
            </a>
            <a
              href={`/exports/appistry-${variant === "badge" ? "a6-badge" : "a5-leaflet"}.pdf`}
              download
              className="px-4 py-2.5 border border-zinc-300 bg-white text-sm font-semibold inline-flex items-center gap-1.5"
              data-testid="download-pdf"
            >
              ⬇ PDF
            </a>
            <a
              href={`/exports/appistry-${variant === "badge" ? "a6-badge" : "a5-leaflet"}.png`}
              download
              className="px-4 py-2.5 border border-zinc-300 bg-white text-sm font-semibold inline-flex items-center gap-1.5"
              data-testid="download-png"
            >
              ⬇ PNG
            </a>
            <button onClick={() => window.print()} className="px-5 py-3 bg-zinc-950 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="print-button">
              Print → <span className="text-xs opacity-70">Cmd/Ctrl+P</span>
            </button>
          </div>
        </div>

        {/* The card */}
        <div className="mx-auto shadow-2xl print-card" data-testid={`event-card-${variant === "badge" ? "a6" : "a5"}`}>

          {/* Top brand strip */}
          <div className={`${size.paddingX} ${size.paddingTop} flex items-start justify-between`}>
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <div className={`flex items-center justify-center ${variant === "badge" ? "h-7 w-7" : "h-9 w-9"}`} style={{ backgroundColor: "#0A0A0A" }}>
                  <span className={`font-black signal-blue ${variant === "badge" ? "text-sm" : "text-base"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>A</span>
                </div>
                <div className={`font-black tracking-tighter ${variant === "badge" ? "text-xl" : "text-2xl"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>Appistry</div>
              </div>
              <p className={`print-mono uppercase tracking-[2.5px] text-zinc-500 mt-2 ${variant === "badge" ? "text-[8px]" : "text-[10px] tracking-[3px] mt-3"}`}>PRACTICAL APPS, THOUGHTFULLY BUILT</p>
            </div>

            <div className="flex flex-col items-end gap-2">
              {/* QR */}
              <div className="bg-white border-2 border-zinc-950" style={{ padding: size.qrPad }}>
                <QRCodeSVG value={URL_TARGET} size={size.qr} bgColor="#FFFFFF" fgColor="#0A0A0A" level="H" />
              </div>
              <p className="text-[7px] print-mono uppercase tracking-widest text-zinc-500">SCAN TO UNLOCK →</p>
            </div>
          </div>

          {/* Headline + sub */}
          <div className={`${size.paddingX} ${variant === "badge" ? "mt-3" : "mt-7"}`}>
            <h2 className={`${size.headlineSize} leading-[1.05] font-black tracking-tighter ${variant === "badge" ? "mb-2" : "mb-3"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
              Niche software,<br/>
              <span className="signal-blue">built like it matters.</span>
            </h2>
            {variant !== "badge" && (
              <p className={`${size.bodySize} leading-relaxed text-zinc-700`}>
                We co-build the unglamorous apps off-the-shelf SaaS has ignored — for therapists, home bakers, residents&rsquo; associations, sole traders, and the small businesses in between.
              </p>
            )}
          </div>

          {/* 3 app tiles */}
          <div className={`${size.paddingX} ${variant === "badge" ? "mt-3" : "mt-6"} grid grid-cols-3 ${size.tilesGap}`}>
            <AppTile compact={variant === "badge"} icon={Sparkle} tone="live" category="LIVE NOW" title="ReflectiveMind" line="AI journaling." />
            <AppTile compact={variant === "badge"} icon={BookOpen} category="CONCEPT" title="Book Club" line="Local reading clubs." />
            <AppTile compact={variant === "badge"} icon={Lock} category="CONCEPT" title="QuietRoom" line="Therapist diary." />
          </div>

          {/* Consultation hook + NFC tap zone */}
          <div className={`${size.paddingX} ${variant === "badge" ? "mt-3" : "mt-5"} flex gap-3 items-stretch`}>
            <div className="signal-bg text-white p-3 flex-1">
              <div className="flex items-start gap-2">
                <Lightning size={variant === "badge" ? 14 : 18} weight="fill" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className={`print-mono uppercase tracking-[2px] opacity-80 font-bold ${variant === "badge" ? "text-[7px]" : "text-[9px] tracking-[2.5px]"}`}>BRING THIS CARD</p>
                  <p className={`font-bold leading-tight mt-0.5 ${variant === "badge" ? "text-[11px]" : "text-base"}`}>Free {minutes}-min Practical Build consultation.</p>
                  {variant !== "badge" && (
                    <p className="text-[11px] mt-1 opacity-90 leading-snug">Walk away with one shippable idea and a plan. Networking-event guests only.</p>
                  )}
                </div>
              </div>
            </div>

            {/* NFC tap zone — sized for a 22mm round NFC sticker. Stick the programmed tag over this circle */}
            <div className="flex flex-col items-center justify-center" data-testid="nfc-zone">
              <div
                className="rounded-full border-2 border-dashed flex flex-col items-center justify-center"
                style={{ width: size.nfc, height: size.nfc, borderColor: "#0A0A0A" }}
              >
                <WifiHigh size={variant === "badge" ? 18 : 22} weight="bold" className="text-zinc-950" style={{ transform: "rotate(45deg)" }} />
                <span className={`print-mono uppercase font-bold text-zinc-950 mt-0.5 ${variant === "badge" ? "text-[6px] tracking-[1px]" : "text-[7px] tracking-[1.5px]"}`}>TAP NFC</span>
              </div>
              <p className={`print-mono uppercase tracking-widest text-zinc-500 mt-1 ${variant === "badge" ? "text-[5px]" : "text-[6px]"}`}>OR ↑</p>
            </div>
          </div>

          {/* Footer */}
          <div className={`absolute bottom-0 inset-x-0 ${size.paddingX} ${variant === "badge" ? "pb-4" : "pb-6"}`}>
            <div className={`border-t border-zinc-200 ${variant === "badge" ? "pt-2" : "pt-3"} flex items-end justify-between`}>
              <div>
                <p className={`font-bold tracking-tight signal-blue ${variant === "badge" ? "text-sm" : "text-base"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{`appistry.tech/event/${code}`}</p>
                <p className={`print-mono uppercase tracking-[2px] text-zinc-500 mt-0.5 ${variant === "badge" ? "text-[7px]" : "text-[9px]"}`}>SCAN OR TAP TO OPEN</p>
              </div>
              <div className="text-right">
                <p className={`print-mono uppercase tracking-[2px] text-zinc-500 ${variant === "badge" ? "text-[7px]" : "text-[9px]"}`}>CO-INNOVATION · H1 2026</p>
                <p className={`font-black tracking-tight ${variant === "badge" ? "text-xs" : "text-base"}`}>3 founding places</p>
              </div>
            </div>
          </div>
        </div>

        {/* Print tips (screen only) */}
        <div className="no-print max-w-2xl mx-auto px-6 mt-8 text-sm text-zinc-600 leading-relaxed">
          <p className="font-semibold text-zinc-950 mb-2">Print tips</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Set your printer to <b>actual size / 100% scale</b> (not "fit to page") so the QR code stays crisp.</li>
            {variant === "badge" ? (
              <>
                <li>To get <b>4 cards per A4 sheet</b>: in the print dialog choose <b>"Multiple pages per sheet"</b> → <b>4 pages per sheet</b> → A4 portrait. 13 sheets gets you 52 cards for the event.</li>
                <li>Print on a <b>200–250 gsm</b> matt cardstock — strong enough to laminate behind a badge.</li>
                <li>The <b>dashed circle</b> is sized for a <b>22mm round NTAG215 NFC sticker</b>. Programme each tag with <span className="print-mono">{URL_TARGET}</span> using NFC Tools (free, iOS &amp; Android), then stick it over the dashed circle. The sticker covers the printed marker neatly.</li>
              </>
            ) : (
              <>
                <li>To get <b>2 leaflets per A4 sheet</b>: in the print dialog choose <b>"Multiple pages per sheet"</b> → <b>2 pages per sheet</b> → A4 portrait.</li>
                <li>Card stock <b>250–300 gsm</b> for a proper feel-in-the-hand finish.</li>
                <li>The <b>dashed circle</b> on the right of the consultation block fits a 22mm NFC sticker if you want to NFC-enable some leaflets too.</li>
              </>
            )}
            <li>Test the QR before the full batch — point your phone at it and confirm it opens <span className="print-mono">{URL_TARGET}</span>.</li>
          </ul>
        </div>
      </div>
    </>
  );
}

function AppTile({ compact, icon: Icon, tone, category, title, line }) {
  const live = tone === "live";
  return (
    <div
      className={`border flex flex-col ${compact ? "p-1.5" : "p-2.5"}`}
      style={{
        borderColor: live ? "#0570DE" : "#E4E4E7",
        backgroundColor: live ? "#EFF6FF" : "#FAFAFA",
        minHeight: compact ? "54px" : "78px",
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <Icon size={compact ? 11 : 14} weight="fill" style={{ color: live ? "#0570DE" : "#0A0A0A" }} />
        <span className={`print-mono uppercase tracking-[1.5px] font-bold ${compact ? "text-[5.5px]" : "text-[7px]"}`} style={{ color: live ? "#0570DE" : "#71717A" }}>
          {live ? "● " : ""}{category}
        </span>
      </div>
      <p className={`font-black tracking-tight leading-tight ${compact ? "text-[10px]" : "text-[12px]"}`} style={{ fontFamily: "'Outfit', sans-serif", color: "#0A0A0A" }}>{title}</p>
      <p className={`leading-snug mt-0.5 text-zinc-600 ${compact ? "text-[7.5px]" : "text-[9px] mt-1"}`}>{line}</p>
    </div>
  );
}
